import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as React from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import * as Yup from "yup";
import axiosInstance from "../../../utils/axiosInstance";
import { modalStyle } from "../../../utils/styles";

export default function ServiceCenterModal({
  open,
  onClose,
  onSubmit,
  serviceCenter,
}) {
  const [thumbnail, setThumbnail] = React.useState([]);
  const [position, setPosition] = React.useState(null);
  const [serviceTypes, setServiceTypes] = React.useState();

  const formik = useFormik({
    initialValues: {
      title: serviceCenter ? serviceCenter.title : "",
      phone: serviceCenter ? serviceCenter.phone : "",
      email: serviceCenter ? serviceCenter.email : "",
      contact_person: serviceCenter ? serviceCenter.contact_person : "",
      latitude: serviceCenter ? serviceCenter.latitude : "",
      longitude: serviceCenter ? serviceCenter.longitude : "",
      service_type_id: serviceCenter ? serviceCenter.service_type_id : -1,
      key_features: serviceCenter
        ? serviceCenter.key_features
        : [{ icon_path: null, description: "" }],
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      phone: Yup.string().nullable(),
      email: Yup.string().email("Invalid email format").nullable(),
      contact_person: Yup.string().nullable(),
      latitude: Yup.number().nullable(),
      longitude: Yup.number().nullable(),
      service_type_id: Yup.string()
        .required("Service type is required")
        .notOneOf(["-1"], "Service center type is required"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();

      if (values.title) formData.append("title", values.title);
      if (values.phone) formData.append("phone", values.phone);
      if (values.email) formData.append("email", values.email);
      if (values.contact_person)
        formData.append("contact_person", values.contact_person);
      if (values.latitude) formData.append("latitude", values.latitude);
      if (values.longitude) formData.append("longitude", values.longitude);
      if (values.service_type_id)
        formData.append("service_type_id", values.service_type_id);

      if (values.key_features && values.key_features.length > 0) {
        values.key_features.forEach((feature, index) => {
          if (feature.description) {
            formData.append(
              `key_features[${index}][description]`,
              feature.description,
            );
          }
          if (feature.icon_path) {
            formData.append(
              `key_features[${index}][icon_path]`,
              feature.icon_path,
            );
          }
        });
      }

      onSubmit(formData, serviceCenter?.id);
      formik.resetForm();
      setThumbnail([]);
      onClose();
    },
  });

  const handleFileChange = (event, index) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue(`key_features[${index}].icon_path`, file);

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newThumbnails = [...thumbnail];
        newThumbnails[index] = e.target.result;
        setThumbnail(newThumbnails);
      };
      reader.readAsDataURL(file);
    }
  };

  function LocationMarker() {
    const map = useMapEvents({
      click(e) {
        setPosition(e.latlng);
        formik.setFieldValue("latitude", e.latlng.lat);
        formik.setFieldValue("longitude", e.latlng.lng);
        map.flyTo(e.latlng, map.getZoom());
      },
    });

    return position === null ? null : (
      <Marker position={position}>
        <Popup>You clicked here</Popup>
      </Marker>
    );
  }

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const fetchServiceCenterTypes = React.useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/service_types`,
      );

      if (response.status === 200) {
        setServiceTypes(response.data);
      }
    } catch (error) {}
  }, []);

  React.useEffect(() => {
    fetchServiceCenterTypes();
  }, [fetchServiceCenterTypes]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography id="modal-modal-title" variant="h6" component="h2">
          {serviceCenter ? "Update Service Center" : "Add New Service Center"}
        </Typography>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              placeholder="Title"
              variant="outlined"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />
            <TextField
              fullWidth
              margin="normal"
              placeholder="Phone"
              variant="outlined"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
            <TextField
              fullWidth
              margin="normal"
              placeholder="Email"
              variant="outlined"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              fullWidth
              margin="normal"
              placeholder="Contact Person"
              variant="outlined"
              name="contact_person"
              value={formik.values.contact_person}
              onChange={formik.handleChange}
              error={
                formik.touched.contact_person &&
                Boolean(formik.errors.contact_person)
              }
              helperText={
                formik.touched.contact_person && formik.errors.contact_person
              }
            />

            <MapContainer center={[9.005401, 38.763611]} zoom={13}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationMarker />
            </MapContainer>

            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                placeholder="Latitude"
                variant="outlined"
                name="latitude"
                value={formik.values.latitude}
                onChange={formik.handleChange}
                error={
                  formik.touched.latitude && Boolean(formik.errors.latitude)
                }
                helperText={formik.touched.latitude && formik.errors.latitude}
              />
              <TextField
                fullWidth
                margin="normal"
                placeholder="Longitude"
                variant="outlined"
                name="longitude"
                value={formik.values.longitude}
                onChange={formik.handleChange}
                error={
                  formik.touched.longitude && Boolean(formik.errors.longitude)
                }
                helperText={formik.touched.longitude && formik.errors.longitude}
              />
            </Box>

            {/* Dropdown for Category */}
            <FormControl fullWidth margin="normal">
              <Select
                id="service_type_id"
                name="service_type_id"
                value={formik.values.service_type_id}
                onChange={formik.handleChange}
                error={
                  formik.touched.service_type_id &&
                  Boolean(formik.errors.service_type_id)
                }
              >
                <MenuItem value={-1}>--Select service center type--</MenuItem>

                {serviceTypes &&
                  serviceTypes.map((serviceType, index) => (
                    <MenuItem key={index} value={serviceType.id}>
                      {serviceType.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Key Features Section */}
            <FieldArray
              name="key_features"
              render={(arrayHelpers) => (
                <div>
                  {formik.values.key_features.map((feature, index) => (
                    <div key={index}>
                      <Typography variant="subtitle1" sx={{ mt: 3 }}>
                        Key Service {index + 1}
                      </Typography>

                      <TextField
                        fullWidth
                        margin="normal"
                        placeholder="Description"
                        variant="outlined"
                        name={`key_features[${index}].description`}
                        value={feature.description}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.key_features &&
                          Boolean(
                            formik.errors.key_features?.[index]?.description,
                          )
                        }
                        helperText={
                          formik.touched.key_features?.[index]?.description &&
                          formik.errors.key_features?.[index]?.description
                        }
                      />

                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        sx={{ mt: 2 }}
                      >
                        {formik.values.key_features[index].icon_path
                          ? "Change Icon"
                          : "Upload Icon"}
                        <VisuallyHiddenInput
                          type="file"
                          onChange={(event) => handleFileChange(event, index)}
                        />
                      </Button>

                      {thumbnail[index] && (
                        <Box
                          component="img"
                          src={thumbnail[index]}
                          alt="Feature Icon"
                          sx={{
                            mt: 2,
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      <Button
                        variant="contained"
                        color="error"
                        sx={{ mt: 2, ml: 2 }}
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        Remove Service
                      </Button>
                    </div>
                  ))}

                  <Button
                    variant="outlined"
                    sx={{ mt: 3 }}
                    onClick={() =>
                      arrayHelpers.push({ icon_path: null, description: "" })
                    }
                  >
                    Add New Key Service
                  </Button>
                </div>
              )}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              {serviceCenter ? "Update Service Center" : "Add Service Center"}
            </Button>
          </form>
        </FormikProvider>
      </Box>
    </Modal>
  );
}
